'use client';

import { BreadcrumbJsonLd } from 'next-seo';
import { cx } from '@linaria/core';
import { withApplicationLayout } from 'hocs/withApplicationLayout';
import { NewNavTabs } from 'modules/Featured/components/NavTabs/NewNavTabs';
import { NewHeader } from 'modules/Featured/components/Header/NewHeader';
import { featuredApi, SessionTokenProp } from 'modules/Featured/components/FeaturedItems/apiEffects';
import { wrapper, extraMargin, newNavbarWrapper, newFooter } from 'modules/Featured/styles';
import { createCookieServerStorage } from 'utils/cookieStorage';
import { buildCommunityBreadcrumbs } from 'utils/breadcrumbs';
import { getDefautServerProps } from 'server/getDefaultServerProps';
import { GetServerSidePropsImpl } from 'utils/server/getserversideprops';
import { GetServerSideProps } from 'next';
import { EntityCategoryWithFilters } from 'modules/Explore/EntityCategoryWithFilters';
import { HomePageFooter } from 'modules/Home/components/HomePageFooter';
import { LRUCache } from 'lru-cache';

const ONE_MINUTE = 1000 * 60;

const cache = new LRUCache({
  max: 100,
  ttl: ONE_MINUTE * 60, // 1 hour cache
});

/**
 * LRU Cache to speed up homepage load times
 * - can only be used for non-personalized data
 * - if the data needs user id or app id, do not use this cache
 */
async function cachedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (cache.has(key)) {
    return cache.get(key) as T;
  }
  const data = await fetcher();
  if (data) {
    cache.set(key, data);
  }
  return data;
}

interface FeaturedPageProps {
  host: string;
  modelData: {
    featuredModels?: CF.API.Models.Model[];
    featuredWorkflows?: CF.API.Workflows.Workflow[];
    llmModels?: CF.API.Models.Model[];
    vlmModels?: CF.API.Models.Model[];
  };
}

const FeaturedPage: React.FC<FeaturedPageProps> = ({ host, modelData: { featuredModels, featuredWorkflows, llmModels, vlmModels } }) => {
  return (
    <>
      <BreadcrumbJsonLd itemListElements={buildCommunityBreadcrumbs({ host, page: 'featured' })} />
      <div className={cx('bound-to-max', extraMargin)}>
        <div className={wrapper}>
          <div className={newNavbarWrapper}>
            <NewNavTabs />
          </div>
          <NewHeader />
        </div>
        <EntityCategoryWithFilters category="featured" resultsToShow={18} initialData={featuredModels} />
        <EntityCategoryWithFilters category="llm" resultsToShow={6} initialData={llmModels} />
        <EntityCategoryWithFilters category="vlm" resultsToShow={6} initialData={vlmModels} />
        <EntityCategoryWithFilters category="workflows" resultsToShow={6} initialData={featuredWorkflows} />
      </div>
      <HomePageFooter footerClassName={newFooter} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = GetServerSidePropsImpl('ExplorePage', async ({ ctx }) => {
  const { req, res, params, query } = ctx;

  if (query?.['refresh-data'] === 'true') {
    // If the query parameter 'refresh-data' is set to true, clear the cache
    cache.clear();
  }

  const host = req ? req.headers.host : 'clarifai.com';
  const sessionToken = createCookieServerStorage(req, res).get('session_token');
  const defautServerProps = await getDefautServerProps({ params, req, res });

  let reqParams: undefined | SessionTokenProp;
  if (sessionToken) {
    // Let us send the session token via request params
    reqParams = { sessionToken };
  }

  const [featuredModelsResponse, featuredWorkflowsResponse, llmModelsResponse, vlmModelsResponse] = await Promise.allSettled([
    cachedFetch('listFeaturedModels', () => featuredApi.listFeaturedModels(reqParams)),
    cachedFetch('listFeaturedWorkflows', () => featuredApi.listFeaturedWorkflows(reqParams)),
    cachedFetch('listLlmModels', () => featuredApi.listLlmModels(reqParams)),
    cachedFetch('listVlmModels', () => featuredApi.listVlmModels(reqParams)),
  ]);

  const featuredModels = featuredModelsResponse.status === 'fulfilled' ? featuredModelsResponse.value : undefined;
  const featuredWorkflows = featuredWorkflowsResponse.status === 'fulfilled' ? featuredWorkflowsResponse.value : undefined;
  const llmModels = llmModelsResponse.status === 'fulfilled' ? llmModelsResponse.value : undefined;
  const vlmModels = vlmModelsResponse.status === 'fulfilled' ? vlmModelsResponse.value : undefined;

  const modelData = {
    featuredModels,
    featuredWorkflows,
    llmModels,
    vlmModels,
  };

  const ssrSafeModelData = Object.fromEntries(Object.entries(modelData).filter(([, value]) => typeof value !== 'undefined'));

  const result = {
    props: {
      ...defautServerProps,
      host,
      modelData: ssrSafeModelData,
      trackEventProps: {
        eventName: 'Community Explore Page Viewed',
      },
      seoProps: {
        title: 'Explore The World’s AI Community | Clarifai',
        description:
          "Explore Clarifai's free community for AI resources. Use our state-of-the-art AI models, workflows and more to add AI into your own applications.",
      },
    },
  };
  return result;
});

// eslint-disable-next-line import/no-default-export
export default withApplicationLayout<FeaturedPageProps>()(FeaturedPage);
